<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import IconHeaderBack from '@/components/svg/IconHeaderBack.vue';
import IconHeaderClose from '@/components/svg/IconHeaderClose.vue';
import { useClientType } from '@/composables/useClientType';
import { getProducts, createOrder } from '@/api/PaymentApi';
import { useMyInfoStore } from '@/store/modules/myinfo';

/**
 * 복지찬스 → 낭만P 전환 페이지 (시연 v2.7 단계4).
 * - 모바일(앱): 풀스크린 / PC: purchase 레이아웃 컨텐츠 영역 안 일반 페이지
 * - 시연 마크업/스타일 기반. 보너스율/환산식은 시연 정책 그대로 (실 정책 확정 시 교체)
 * - 진입은 smartwel 회원에 한정 (guards에서 일반 /purchase/points 진입을 redirect)
 * - "낭만충전"은 from=exchange 쿼리로 guards 우회
 * - 전환은 SMARTWEL 가변 상품(createOrder)으로 처리 (서버 간 복지포인트 직접 차감)
 */
definePage({ meta: { layout: 'purchase', title: '복지포인트 전환' } });

const router = useRouter();
const route = useRoute();
const { partner } = useMember();
const { isMobileUi } = useClientType();
const myInfoStore = useMyInfoStore();

// SMARTWEL 상품 목록 (가변 + 고정)
const smartwelProducts = ref([]);
const variableProduct = computed(() =>
  smartwelProducts.value.find((p) => p.detail?.[0]?.isVariableAmount),
);
const variableProductDetail = computed(() => variableProduct.value?.detail?.[0] ?? {});
// 빠른 선택용 고정 상품 (가변 제외, price 오름차순)
const fixedSmartwelProducts = computed(() =>
  smartwelProducts.value
    .filter((p) => !p.detail?.[0]?.isVariableAmount)
    .slice()
    .sort((a, b) => (a.price ?? 0) - (b.price ?? 0)),
);

const fetchSmartwelProducts = async () => {
  try {
    const data = await getProducts({ platform: 'SMARTWEL', productType: 'POINT', size: -1 });
    const all = data?.content ?? [];
    // 표시 가능한 상품만 (BE 미필터링 대비 안전장치)
    smartwelProducts.value = all.filter(
      (p) => p.isPublic && p.isAvailable && p.status === 'ACTIVE',
    );
    console.log('[exchange] smartwelProducts', smartwelProducts.value);
  } catch (e) {
    console.log('[exchange] fetchSmartwelProducts error', e);
  }
};
onMounted(async () => {
  await fetchSmartwelProducts();
  applyShortageQuery();
});

/** 환산: 5원 = 1P (소숫점 버림) */
const wonToNangman = (won) => Math.floor(won / 5);

/**
 * 가변 상품에 정의된 보너스 구간(bonusTiers) — threshold 오름차순 정렬
 * - 각 tier: { threshold(이 금액 이상부터), point(지급 보너스 낭만P), label, earnSource }
 */
const bonusTiers = computed(() => {
  const tiers = variableProductDetail.value?.bonusTiers ?? [];
  return tiers.slice().sort((a, b) => (a.threshold ?? 0) - (b.threshold ?? 0));
});

/**
 * 입력 금액에 매칭되는 보너스 구간.
 * - 입력값 이상(threshold <= amount) 중 "가장 큰 threshold" tier를 적용
 */
const getBonusTier = (targetAmount) => {
  let matched = null;
  for (const t of bonusTiers.value) {
    if ((t.threshold ?? 0) <= targetAmount) matched = t;
    else break;
  }
  return matched;
};

const matchedBonusTier = computed(() => getBonusTier(amount.value));

const getBonusLabel = (tier) => {
  if (!tier) return '';
  if (tier.label) return tier.label;

  const threshold = Number(tier.threshold ?? 0);
  const point = Number(tier.point ?? 0);
  if (!threshold || !point) return '';

  return `+${Math.round((point / (threshold / 5)) * 100)}%`;
};

const balance = computed(() => partner.value?.totalPoint ?? 0);

/** 가변 상품 기준 1회 전환 가능 범위. 미제공 시 기존 정책값을 사용한다. */
const MIN_EXCHANGE = 100;
const MAX_EXCHANGE = 100000;
const toPositiveNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const minExchangeAmount = computed(() =>
  toPositiveNumber(variableProductDetail.value?.minAmount, MIN_EXCHANGE),
);
const maxExchangeAmount = computed(() =>
  toPositiveNumber(variableProductDetail.value?.maxAmount, MAX_EXCHANGE),
);
/** 전액 전환 시 적용 금액 (잔액과 최대 한도 중 작은 값) */
const fullExchangeAmount = computed(() => Math.min(balance.value, maxExchangeAmount.value));

const amount = ref(0);
const selectedQuick = ref(null);
const selectedProductId = ref(null);
const inputValue = ref('');
const inputFocused = ref(false);
const isFull = ref(false);

/**
 * 빠른 선택 금액.
 * - 가변 상품 bonusTiers가 있으면 threshold를 전환 금액으로 사용
 * - 없으면 기존 고정 상품 기반 quick card를 fallback으로 유지
 */
const QUICK_AMOUNTS = computed(() => {
  if (variableProduct.value && bonusTiers.value.length) {
    return bonusTiers.value.map((tier) => {
      const value = Number(tier.threshold ?? 0);
      const bonusPoint = Number(tier.point ?? 0);
      return {
        value,
        key: `${variableProduct.value.productId}-${value}`,
        label: `${value.toLocaleString()} P`,
        bonus: getBonusLabel(tier),
        productId: variableProduct.value.productId,
        totalPoint: wonToNangman(value) + bonusPoint,
      };
    });
  }

  return fixedSmartwelProducts.value.map((p) => {
    const detail = p.detail?.[0] ?? {};
    const base = Number(detail.point ?? 0);
    const bonus = Number(detail.bonusPoint ?? 0);
    const pct = base > 0 ? Math.round((bonus / base) * 100) : 0;
    const price = Number(p.price ?? 0);
    // 모바일은 좁아서 '+N%'만, PC는 '+N% 보너스'까지 표시
    const bonusText = pct > 0 ? (isMobileUi.value ? `+${pct}%` : `+${pct}% 보너스`) : '';
    return {
      value: price,
      key: p.productId,
      label: `${price.toLocaleString()} P`,
      bonus: bonusText,
      productId: p.productId,
      totalPoint: base + bonus,
    };
  });
});

/**
 * shortageAmount (낭만P 부족분) 쿼리로 진입한 경우 매칭 금액을 자동 선택
 * - 보너스 포함 totalPoint가 부족분 이상인 가장 작은 빠른 선택 항목
 * - 모든 항목으로 못 채우면 최고 금액 항목 선택
 * - 사용 가능한 복지P/최대 한도를 넘는 항목은 선택 보류
 */
const applyShortageQuery = () => {
  const shortage = Number(route.query.shortageAmount) || 0;
  if (shortage <= 0) return;

  if (variableProduct.value && bonusTiers.value.length) {
    const maxAmount = fullExchangeAmount.value;
    if (maxAmount < minExchangeAmount.value) return;

    const startAmount = Math.ceil(minExchangeAmount.value / 100) * 100;
    let candidateAmount = null;
    for (let nextAmount = startAmount; nextAmount <= maxAmount; nextAmount += 100) {
      const bonusPoint = Number(getBonusTier(nextAmount)?.point ?? 0);
      if (wonToNangman(nextAmount) + bonusPoint >= shortage) {
        candidateAmount = nextAmount;
        break;
      }
    }

    setQuick({
      value: candidateAmount ?? Math.floor(maxAmount / 100) * 100,
      productId: variableProduct.value.productId,
    });
    return;
  }

  const list = QUICK_AMOUNTS.value;
  if (!list.length) return;

  let candidate = list.find((item) => item.totalPoint >= shortage);
  if (!candidate) candidate = list[list.length - 1];
  if (candidate.value > fullExchangeAmount.value) return;

  setQuick(candidate);
};

const baseP = computed(() => wonToNangman(amount.value));
/**
 * bonusTiers 미제공 환경 대비: 입력 금액 이하 "가장 큰 price 고정상품"의 보너스 사용.
 * 가변 bonusTiers가 제공되면 가변 구간을 우선한다.
 */
const matchedFixedProduct = computed(() => {
  if (bonusTiers.value.length) return null;

  let matched = null;
  for (const p of fixedSmartwelProducts.value) {
    if ((p.price ?? 0) <= amount.value) matched = p;
    else break;
  }
  return matched;
});
const bonusP = computed(() => {
  if (bonusTiers.value.length) return Number(matchedBonusTier.value?.point ?? 0);
  return Number(matchedFixedProduct.value?.detail?.[0]?.bonusPoint ?? 0);
});
const totalP = computed(() => baseP.value + bonusP.value);

/** 잔액 부족 여부 */
const isOverBalance = computed(() => amount.value > balance.value);
const isOverMaxAmount = computed(() => amount.value > maxExchangeAmount.value);
const isUnderMinAmount = computed(
  () => amount.value > 0 && amount.value < minExchangeAmount.value,
);

/** 입력 단위(100P) · 최저 전환 금액 위반 여부 */
const isInvalidUnit = computed(
  () => amount.value > 0 && (isUnderMinAmount.value || amount.value % 100 !== 0),
);

/** 입력값 오류 (테두리·hint 경고색) */
const hasError = computed(
  () => isOverBalance.value || isOverMaxAmount.value || isInvalidUnit.value,
);

/** CTA 활성화: 100P 단위·최저/최대 금액 충족 + 잔액 충분 */
const ctaEnabled = computed(
  () =>
    amount.value >= minExchangeAmount.value &&
    amount.value <= maxExchangeAmount.value &&
    amount.value % 100 === 0 &&
    !isOverBalance.value,
);

/** 입력 hint 텍스트 */
const inputHint = computed(() => {
  if (isOverBalance.value) return `잔액(${balance.value.toLocaleString()}P)을 초과했어요`;
  if (isOverMaxAmount.value) return `최대 ${maxExchangeAmount.value.toLocaleString()}P까지 전환할 수 있어요`;
  if (isInvalidUnit.value) return `입력 단위 100P, 최소 입력 포인트 ${minExchangeAmount.value.toLocaleString()}P`;
  return `100P 단위, ${minExchangeAmount.value.toLocaleString()}P부터 ${maxExchangeAmount.value.toLocaleString()}P까지`;
});

/** 표시 포맷
 *  - withUnit=true (blur 상태): "1,000 P"
 *  - withUnit=false (focus/raw): "1000" — 편집 자연스러움 */
const formatNumber = (num) => (num ? num.toLocaleString() : '');
/** 입력란 표시값: 단위(P)는 값에 굽지 않고 별도 .input-unit span으로 표기 → 포커스/블러 모두 숫자 우측 정렬 일관 */
const formatInputValue = (num) => formatNumber(num);

/** 빠른 금액 클릭 → 입력란에 반영 */
const setQuick = (item) => {
  selectedQuick.value = item.value;
  selectedProductId.value = item.productId ?? null;
  amount.value = item.value;
  inputValue.value = formatInputValue(item.value);
  isFull.value = false;
};

const onInputChange = (e) => {
  // 입력 중에는 raw 숫자만 유지 (콤마 없음 → 편집 자연스러움)
  const raw = String(e.target.value).replace(/[^0-9]/g, '');
  amount.value = Number(raw) || 0;
  inputValue.value = raw;
  selectedQuick.value = null;
  selectedProductId.value = null;
  isFull.value = false;
};

const onInputFocus = () => {
  inputFocused.value = true;
  // 편집 시작 시 콤마 제거 → raw 숫자로 (커서/백스페이스 자연스럽게)
  inputValue.value = amount.value ? String(amount.value) : '';
};
const onInputBlur = () => {
  inputFocused.value = false;
  // 편집 종료 시 콤마 + P 포맷 (input value에 포함)
  inputValue.value = formatInputValue(amount.value);
};

/** 전액 전환 → 잔액 전체(최대 10만)를 입력란에 반영 (토글) */
const toggleFull = () => {
  if (isFull.value) {
    isFull.value = false;
    amount.value = 0;
    inputValue.value = '';
  } else {
    isFull.value = true;
    amount.value = fullExchangeAmount.value;
    inputValue.value = formatInputValue(fullExchangeAmount.value);
    selectedQuick.value = null;
    selectedProductId.value = null;
  }
};

const showBreakdown = computed(() => amount.value > 0);

const onBack = () => {
  if (window.history.length > 1) router.back();
  else router.replace('/main');
};

const onClose = () => {
  router.replace('/main');
};

const onNangmanCharge = () => {
  // guards에서 smartwel 회원의 /purchase/points 진입을 막으므로 from=exchange로 우회
  router.push({ path: '/purchase/points', query: { from: 'exchange' } });
};

const isPurchasing = ref(false);

const onExchange = async () => {
  if (!ctaEnabled.value || isPurchasing.value) return;
  // 빠른 선택(고정 상품) 우선, 없으면 직접 입력용 가변 상품
  const fixed = fixedSmartwelProducts.value.find(
    (p) => p.productId === selectedProductId.value,
  );
  const product = fixed ?? variableProduct.value;
  if (!product) {
    alert('전환 가능한 상품이 없습니다. 잠시 후 다시 시도해주세요.');
    return;
  }
  isPurchasing.value = true;
  try {
    await createOrder({
      amount: amount.value,
      pointAmount: 0,
      totalAmount: amount.value,
      productId: product.productId,
      orderName: `복지찬스 ${amount.value.toLocaleString()}P 전환`,
    });
    // 복지포인트 잔액 갱신
    await myInfoStore.fetchMyInfo();
    router.push({
      path: '/purchase/nmspFinishPointEnd',
      query: { next: route.query?.next ?? '/main' },
    });
  } catch (e) {
    const msg = e?.response?.data?.detail || '전환 처리 중 오류가 발생했습니다. 다시 시도해주세요.';
    alert(msg);
  } finally {
    isPurchasing.value = false;
  }
};
</script>

<template>
  <div
    class="smartwel-exchange"
    :class="[
      isMobileUi ? 'smartwel-exchange--mobile' : 'smartwel-exchange--pc',
      { 'kb-open': isMobileUi && inputFocused },
    ]"
  >
    <!-- 모바일(앱): 풀스크린 전용 헤더 -->
    <div v-if="isMobileUi" class="page-header">
      <div class="page-header-left">
        <button type="button" class="page-back" aria-label="뒤로" @click="onBack">
          <IconHeaderBack />
        </button>
        <span class="page-title">복지포인트 전환</span>
      </div>
      <div class="page-header-right">
        <button type="button" class="btn-nangman-charge" @click="onNangmanCharge">낭만P 구매</button>
        <button type="button" class="page-close" aria-label="닫기" @click="onClose">
          <IconHeaderClose />
        </button>
      </div>
    </div>

    <!-- PC: 컨텐츠 상단 인라인 헤더 (제목 + 낭만충전) -->
    <div v-else class="pc-header">
      <span class="pc-header__title">복지포인트 전환</span>
      <button type="button" class="btn-nangman-charge" @click="onNangmanCharge">
        직접 낭만P 구매하러 가기
      </button>
    </div>

    <div class="scroll-area">
      <!-- 잔액 카드 -->
      <div class="balance-card">
        <div class="balance-card__info">
          <div class="balance-label">보유 복지포인트</div>
          <div v-if="!isMobileUi" class="balance-subinfo">전환할 수 있는 포인트입니다</div>
        </div>
        <div class="balance-value-row">
          <span class="bok-p-icon">P</span>
          <span class="balance-value"
            >{{ balance.toLocaleString() }}<span class="unit">P</span></span
          >
        </div>
      </div>

      <!-- 빠른 금액 선택 -->
      <div class="box-label box-label--quick">
        <span class="box-label-left">
          <span v-if="isMobileUi" class="bok-p-icon small">P</span>전환할 복지포인트 (빠른 선택)
        </span>
      </div>
      <div class="quick-amounts">
        <button
          v-for="q in QUICK_AMOUNTS"
          :key="q.key"
          type="button"
          class="quick-card"
          :class="{ active: selectedQuick === q.value }"
          @click="setQuick(q)"
        >
          <span class="quick-card__label"
            >{{ q.value.toLocaleString() }}<span class="quick-card__unit">P</span></span
          >
          <span v-if="q.bonus" class="quick-card__bonus">{{ q.bonus }}</span>
        </button>
      </div>
      <p class="bonus-tip">
        포인트 전환 시 <span class="emphasis">보너스 포인트</span>를 드려요
      </p>

      <!-- PC: 직접 입력 섹션 헤더 + 전액 전환 (시안 v3, 2단 영역 위로 분리) -->
      <div v-if="!isMobileUi" class="exchange-head">
        <span class="exchange-head__label">전환 내역 (직접 입력 가능)</span>
        <button
          type="button"
          class="max-btn-outside"
          :class="{ active: isFull }"
          @click="toggleFull"
        >
          <span class="max-btn-outside__check" aria-hidden="true">✓</span>전액 전환
        </button>
      </div>

      <!-- 입력 → 결과 (PC: 가로 2단 / 모바일: 세로 스택) -->
      <div class="exchange-area">
        <!-- 직접 입력 -->
        <div class="input-section">
          <div class="box-label">
            <span class="box-label-left">
              <span class="bok-p-icon small">P</span>
              {{ isMobileUi ? '전환할 복지포인트 (직접 입력)' : '전환할 복지포인트' }}
            </span>
            <!-- 모바일: 전액 전환을 라벨행에 유지 / PC: 상단 섹션 헤더로 분리 -->
            <button
              v-if="isMobileUi"
              type="button"
              class="use-all-btn"
              :class="{ active: isFull }"
              @click="toggleFull"
            >
              전액 전환
            </button>
          </div>
          <label class="input-wrap" :class="{ focused: inputFocused, error: hasError }">
            <input
              type="text"
              class="input-field"
              placeholder="0"
              inputmode="numeric"
              :size="Math.max((inputValue || '0').length, 1)"
              :value="inputValue"
              @input="onInputChange"
              @focus="onInputFocus"
              @blur="onInputBlur"
            />
            <span class="input-unit">P</span>
          </label>
          <div class="input-hint" :class="{ error: isMobileUi && hasError }">{{ inputHint }}</div>
        </div>

        <div class="arrow-divider">
          <span class="arrow-divider__icon">{{ isMobileUi ? '↓' : '→' }}</span>
        </div>

        <!-- 받는 낭만포인트 -->
        <div class="input-section result-section">
          <div class="box-label">
            <span class="box-label-left"> <i class="nangman-p-icon" />받는 낭만포인트 합계 </span>
          </div>
          <div class="result-row">
            <span class="result-value">{{ totalP.toLocaleString() }} <span class="result-unit">P</span></span>
          </div>

          <!-- 환산 명세 -->
          <div v-if="showBreakdown" class="breakdown-box">
            <div class="breakdown-row">
              <span class="breakdown-label">전환되는 낭만포인트</span>
              <span class="breakdown-value">{{ baseP.toLocaleString() }}P</span>
            </div>
            <div v-if="bonusP > 0" class="breakdown-row bonus-row">
              <span class="breakdown-label">+ 보너스 포인트</span>
              <span class="breakdown-value bonus">+{{ bonusP.toLocaleString() }}P</span>
            </div>
          </div>
        </div>
      </div>

      <div class="notice">
        <template v-if="isMobileUi">
          <span class="icon">⚠️</span>
          <span>전환한 포인트는 환불되지 않아요</span>
        </template>
        <template v-else>
          <span class="icon">ⓘ</span>
          <span>전환한 포인트는 환불되지 않습니다. 신중히 결정해주세요.</span>
        </template>
      </div>
    </div>

    <div class="cta-area">
      <button
        type="button"
        class="cta-btn"
        :disabled="!ctaEnabled || isPurchasing"
        @click="onExchange"
      >
        {{
          isPurchasing
            ? '처리 중...'
            : ctaEnabled
              ? `${totalP.toLocaleString()} 낭만P로 전환하기`
              : '전환하기'
        }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.smartwel-exchange {
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #1f2937;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', 'Pretendard Variable', sans-serif;
  letter-spacing: -0.3px;
  word-break: keep-all;
}

/* 모바일(앱): 풀스크린 */
.smartwel-exchange--mobile {
  position: fixed;
  inset: 0;
  z-index: 100;
  padding-top: var(--safe-area-top, 0px);
}

/* PC: purchase 레이아웃 컨텐츠 영역 안 일반 페이지 */
.smartwel-exchange--pc {
  width: 100%;
  border-radius: 12px;
  /* 하단 sticky CTA 바에 컨텐츠가 가려지지 않도록 여백 확보 (바: 상하 28px + 버튼 높이) */
  padding-bottom: 116px;
}

/* PC 컨텐츠 상단 인라인 헤더 (제목 + 낭만충전) */
.pc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 40px 0 16px;
}

.pc-header__title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

/* Header (모바일 풀스크린) — 서비스구매 레이아웃 .com-app-tit-box 와 동일 높이/패딩 */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 16px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-back {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.page-title {
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  color: var(--Gray-800, #333);
}

.page-header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn-nangman-charge {
  padding: 7px 12px;
  background: #fff;
  color: #1e3a5f;
  border: 1.5px solid #1e3a5f;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: -0.3px;
  transition: all 0.2s;

  &:hover {
    background: #1e3a5f;
    color: #fff;
  }
}

/* PC: 타이틀 우측 버튼 — 시안 alt-button (흰 배경·회색 테두리, hover 코랄) */
.pc-header .btn-nangman-charge {
  padding: 9px 16px;
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;

  &:hover {
    background: #fff;
    border-color: var(--orange-300-main, #eb4c10);
    color: var(--orange-300-main, #eb4c10);
  }
}

.page-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

/* IconHeader 내부 span.icon이 inline이라 정렬에 영향 주는 것 보정 */
.page-back :deep(.icon),
.page-close :deep(.icon) {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

/* Scroll body */
.scroll-area {
  padding: 16px 20px 20px;

  &::-webkit-scrollbar {
    width: 0;
  }
}

/* 모바일: 풀스크린 내 스크롤 영역 */
.smartwel-exchange--mobile .scroll-area {
  flex: 1;
  overflow-y: auto;
}

/* PC: 일반 흐름 (좌우 패딩만, 외부 페이지 스크롤) */
.smartwel-exchange--pc .scroll-area {
  padding: 0;
}

/* 잔액 카드 */
.balance-card {
  position: relative;
  background: linear-gradient(135deg, #1e3a5f 0%, #2c4a75 100%);
  border-radius: 14px;
  padding: 14px 16px;
  color: #fff;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -20px;
    right: -20px;
    width: 90px;
    height: 90px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%);
    border-radius: 50%;
  }
}

.balance-label {
  position: relative;
  z-index: 1;
  font-size: 12.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
}

/* PC: 잔액 카드 — 시안 옅은 코발트 카드 (포인트 아이콘 .bok-p-icon은 원래대로 유지) */
.smartwel-exchange--pc .balance-card {
  background: #eef2f7;
  border: 1px solid #dce4ee;
  border-radius: 12px;
  padding: 22px 26px;
  color: #1e3a5f;
}

/* PC: 네이비 카드의 광택(radial) 효과 제거 */
.smartwel-exchange--pc .balance-card::before {
  display: none;
}

.smartwel-exchange--pc .balance-label {
  color: #1e3a5f;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 6px;
}

.smartwel-exchange--pc .balance-subinfo {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
}

.smartwel-exchange--pc .balance-value-row {
  gap: 10px;
}

.smartwel-exchange--pc .balance-value {
  color: #1e3a5f;
  font-size: 28px;
  font-weight: 800;

  .unit {
    color: #475569;
    font-size: 16px;
    font-weight: 700;
    opacity: 1;
  }
}

.balance-value-row {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6px;
}

.bok-p-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #3b82f6;
  color: #fff;
  font-family: Arial, sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;

  &.small {
    width: 16px;
    height: 16px;
    font-size: 10px;
  }
}

.balance-value {
  font-size: 20px;
  font-weight: 700;
  color: #fff;

  .unit {
    font-size: 13px;
    font-weight: 500;
    margin-left: 2px;
    opacity: 0.85;
  }
}

/* 빠른 금액 */
.quick-amounts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  column-gap: 8px;
  padding-top: 4px;
  margin-bottom: 8px;
}

.quick-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;

  &:hover {
    border-color: #c7d2de;
  }

  &.active {
    background: #f0f7ff;
    border-color: #1e3a5f;

    .quick-card__label {
      color: #1e3a5f;
    }
  }
}

.quick-card__label {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  letter-spacing: -0.3px;
}

.quick-card__unit {
  font-size: 11px;
  font-weight: 400;
  margin-left: 2px;
  color: #1f2937;
}

/* CustomRadiosPoint의 .discount-percent와 동일한 톤 */
.quick-card__bonus {
  color: #ff6b6b;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  background: rgba(255, 107, 107, 0.1);
  padding: 2px 4px;
  border-radius: 4px;
  white-space: nowrap;
}

/* 폴드 unfolded·태블릿 이상: 4열 그리드 */
@media (min-width: 500px) {
  .quick-amounts {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* PC·태블릿 등 넓은 폭: 패딩 넓히고 폰트 키움 */
@media (min-width: 768px) {
  .quick-card {
    padding: 20px;
  }
  .quick-card__label {
    font-size: 20px;
    font-weight: 700;
  }
}


.bonus-tip {
  font-size: 11.5px;
  color: #6b7280;
  margin-top: 0;
  margin-bottom: 16px;
  text-align: left;
  font-weight: 500;

  .emphasis {
    color: var(--orange-300-main, #eb4c10);
    font-weight: 600;
  }
}

/* 입력 섹션 */
.input-section {
  &.result-section {
    margin-bottom: 16px;
  }
}

.box-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
}

.box-label-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* PC: 빠른 선택 레이블 — 시안 section-label (포인트 아이콘 없음, 14px/700) */
.smartwel-exchange--pc .box-label--quick {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 14px;
}

.use-all-btn {
  flex-shrink: 0;
  background: transparent;
  border: none;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 700;
  color: #1e3a5f;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    background: #f0f7ff;
  }

  &.active {
    background: #1e3a5f;
    color: #fff;
    text-decoration: none;
  }
}

.input-wrap {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding: 9px 14px;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  transition: border-color 0.2s;
  cursor: text;

  &.focused {
    border-color: #1e3a5f;
  }

  &.error {
    border-color: #dc2626;
    background: #fef2f2;
  }
}

.input-field {
  flex: 0 1 auto;
  min-width: 1ch;
  max-width: 100%;
  background: transparent;
  border: none;
  outline: none;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  /* 단위(P)를 별도 span으로 분리 → 숫자를 input 우측 끝에 붙여 P와의 간격 일정하게 (PC는 .input-field에서 left로 override) */
  text-align: right;
  letter-spacing: -0.3px;
  font-family: inherit;

  &::placeholder {
    color: #d1d5db;
  }
}

.input-unit {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  letter-spacing: -0.3px;
  flex-shrink: 0;
}

.input-hint {
  margin-top: 4px;
  font-size: 11.5px;
  color: #6b7280;
  font-weight: 500;

  &.error {
    color: #dc2626;
    font-weight: 600;
  }
}

.arrow-divider {
  text-align: center;
  font-size: 14px;
  color: #9ca3af;
  line-height: 16px;
}

/* PC: 입력 → 결과 가로 2단 배치 (모바일은 기존 세로 스택 유지) */
.smartwel-exchange--pc .exchange-area {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  margin-bottom: 16px;
}

/* 2단일 때 결과 섹션 하단 여백 제거 (그리드 정렬 위해) */
.smartwel-exchange--pc .exchange-area .input-section.result-section {
  margin-bottom: 0;
}

/* PC: 가운데 화살표(→) 세로 가운데 정렬 */
.smartwel-exchange--pc .arrow-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 18px;
}

/* PC: 시안 원형 화살표 (네이비 44px 원 + 흰 →) */
.smartwel-exchange--pc .arrow-divider__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #1e3a5f;
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.15);
}

/* PC: 입력/결과를 카드로 (테두리 + 패딩) */
.smartwel-exchange--pc .exchange-area .input-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 22px 24px;
}

/* PC: 직접 입력 섹션 헤더 (라벨 + 전액 전환 버튼) */
.smartwel-exchange--pc .exchange-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
}

.smartwel-exchange--pc .exchange-head__label {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
}

/* PC: 전액 전환 버튼 — 비활성 옅은 코발트 / 활성 네이비 채움 (체크박스형) */
.smartwel-exchange--pc .max-btn-outside {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 8px 16px;
  background: #eef2f7;
  color: #1e3a5f;
  border: 1.5px solid #1e3a5f;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #dce4ee;
  }

  &.active {
    background: #1e3a5f;
    color: #fff;
    border-color: #1e3a5f;

    &:hover {
      background: #15294a;
    }
  }
}

.smartwel-exchange--pc .max-btn-outside__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1.5px solid #1e3a5f;
  border-radius: 4px;
  background: transparent;
  color: transparent;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
}

.smartwel-exchange--pc .max-btn-outside.active .max-btn-outside__check {
  background: #fff;
  border-color: #fff;
  color: #1e3a5f;
}

/* PC: 입력란 — 밑줄형, 좌측 정렬, 큰 폰트 (시안 v3) */
.smartwel-exchange--pc .input-wrap {
  justify-content: flex-start;
  align-items: baseline;
  gap: 4px;
  padding: 6px 0;
  border: none;
  border-bottom: 2px solid #1f2937;
  border-radius: 0;

  &.focused {
    border-bottom-color: #1e3a5f;
  }

  &.error {
    background: transparent;
    border-bottom-color: #dc2626;
  }
}

.smartwel-exchange--pc .input-field {
  flex: 1;
  width: 100%;
  max-width: none;
  /* 브라우저 기본 input padding 제거 → 결과 값(span)과 숫자 윗선 정확히 일치 */
  padding: 0;
  font-size: 28px;
  font-weight: 800;
  text-align: left;
}

.smartwel-exchange--pc .input-unit {
  font-size: 18px;
  font-weight: 700;
  color: #6b7280;
}

/* PC: 결과 카드 내부 박스 테두리 제거 (카드가 프레임 역할) + 좌측 정렬 */
.smartwel-exchange--pc .result-row {
  justify-content: flex-start;
  align-items: baseline;
  /* 입력란(밑줄, padding 6px 0)과 동일한 상단 패딩으로 입력/결과 값 높이 정렬 */
  padding: 6px 0;
  border: none;
  border-radius: 0;
}

/* PC: 전환 내역 카드 레이블 — 시안 card-label (12.5px/700, 입력=회색 / 결과=네이비) */
.smartwel-exchange--pc .exchange-area .box-label {
  font-size: 12.5px;
  font-weight: 700;
  margin-bottom: 14px;
}

.smartwel-exchange--pc .result-section .box-label {
  color: #1e3a5f;
}

/* PC: 받는 낭만포인트 값 — 시안 output (28px 네이비 + 단위 18px) */
.smartwel-exchange--pc .result-value {
  font-size: 28px;
  font-weight: 800;
  color: #1e3a5f;
}

.smartwel-exchange--pc .result-unit {
  font-size: 18px;
  font-weight: 700;
  color: #1e3a5f;
}

/* PC: 환산 명세(기본 전환·보너스 포인트) — 시안 bonus-detail (배경 없이 점선 구분선만) */
.smartwel-exchange--pc .breakdown-box {
  margin-top: 12px;
  padding: 10px 0 0;
  background: transparent;
  border-radius: 0;
  border-top: 1px dashed #e5e7eb;
}

/* 결과 */
.result-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  padding: 9px 14px;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
}

/* 받는 낭만포인트 레이블 아이콘 (복지P 뱃지와 동일 크기) */
.nangman-p-icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  background: url('@/assets/images_manse/point.svg') no-repeat center / contain;
  flex-shrink: 0;
}

.result-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  letter-spacing: -0.3px;
}

/* breakdown */
.breakdown-box {
  margin-top: 8px;
  padding: 12px 14px;
  background: #f9fafb;
  border-radius: 10px;
}

.breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 0;
  font-size: 12.5px;

  &.bonus-row .breakdown-label {
    color: var(--orange-300-main, #eb4c10);
    font-weight: 700;
  }

  &.total-row {
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px dashed #e5e7eb;
  }
}

.breakdown-label {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #6b7280;
  font-weight: 500;

  &.total {
    font-size: 13px;
    color: #1f2937;
    font-weight: 700;
  }
}

.breakdown-value {
  color: #4b5563;
  font-weight: 600;

  &.bonus {
    color: var(--orange-300-main, #eb4c10);
    font-weight: 700;
  }

  &.total {
    font-size: 15px;
    color: #d85a30;
    font-weight: 700;
  }
}

/* 안내 */
.notice {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 12px;
  margin-bottom: 12px;
  background: #fef3e2;
  border-radius: 8px;
  font-size: 11.5px;
  color: #92400e;
  font-weight: 500;

  .icon {
    font-size: 13px;
  }
}

/* PC: 안내(notice) — 시안 회색 톤 (ⓘ 아이콘, 중립 회색 배경/텍스트) */
.smartwel-exchange--pc .notice {
  padding: 10px 14px;
  background: #f9fafb;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
}

/* CTA */
.cta-area {
  border-top: 1px solid #f3f4f6;
  background: #fff;
  flex-shrink: 0;
}

/* 모바일: 풀스크린 하단 (safe-area 포함) */
.smartwel-exchange--mobile .cta-area {
  padding: 12px 20px calc(var(--safe-area-bottom, 0px) + 16px);
  transition: padding-bottom 0.15s;
}

/* 키보드 활성 시: safe-area 제거해서 키보드와 버튼 사이 여백 최소화 */
.smartwel-exchange--mobile.kb-open .cta-area {
  padding-bottom: 12px;
}

/* PC: 하단 sticky 고정 결제 바 (시안 v3) — 상하 패딩은 낭만P 충전(points.vue .fixed-pay-box)과 동일하게 28px */
.smartwel-exchange--pc .cta-area {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  padding: 28px 40px calc(28px + var(--safe-area-bottom, 0px));
  background: #fff;
  border-top: 1px solid #f3f4f6;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.06);
}

/* PC: 버튼은 중앙 정렬 + 최대폭 제한 */
.smartwel-exchange--pc .cta-btn {
  display: block;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
}

.cta-btn {
  width: 100%;
  background: #1e3a5f;
  color: #fff;
  border: none;
  padding: 14px;
  border-radius: 12px;
  font-size: 15.5px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #2c4a75;
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }
}
</style>
